import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { MapPin, Heart, User, Menu, X, LogOut, ShieldCheck, Building2, Globe, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { LocationSelector } from "./LocationSelector";

const NAV = [
  { label: "Accueil", to: "/", id: "accueil" },
  { label: "Explorer", to: "/explorer", id: "explorer" },
  { label: "Ce soir", to: "/ce-soir", id: "ce-soir" },
  { label: "Événements", to: "/evenements", id: "evenements" },
  { label: "Hébergements", to: "/hebergements", id: "hebergements" },
];

export const LangDropdown = ({ onDone, itemClass = "flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm transition hover:bg-slate-100" }) => {
  const { lang, setLang, t } = useLanguage();
  const pick = (l) => {
    setLang(l);
    onDone?.();
  };
  return (
    <>
      <button type="button" data-testid="lang-option-fr" onClick={() => pick("fr")} className={`${itemClass} ${lang === "fr" ? "font-semibold text-[#2563EB]" : "text-slate-700"}`}>
        Français
        {lang === "fr" && <Check className="h-4 w-4" />}
      </button>
      <button type="button" data-testid="lang-option-en" onClick={() => pick("en")} className={`${itemClass} ${lang === "en" ? "font-semibold text-[#2563EB]" : "text-slate-700"}`}>
        English
        {lang === "en" && <Check className="h-4 w-4" />}
      </button>
    </>
  );
};

export const Header = () => {
  const { user, logout, favorites } = useAuth();
  const { t } = useLanguage();
  const [cities, setCities] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const navigate = useNavigate();
  const favCount = Object.keys(favorites).length;

  const { pathname } = useLocation();

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
  }, []);

  const navClass = ({ isActive }) =>
    `rounded-full px-4 py-2 text-sm font-medium transition ${
      isActive ? "bg-[#EFF6FF] text-[#2563EB]" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
    }`;

  if (pathname.startsWith("/pro") || pathname.startsWith("/admin")) return null;

  return (
    <header data-testid="header" className="sticky top-0 z-50 border-b border-slate-200/60 bg-white/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-[1800px] items-center justify-between gap-3 px-4 sm:px-6 lg:px-10">
        <Link to="/" data-testid="header-logo" className="flex shrink-0 items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-[#2563EB] to-[#06B6D4]">
            <MapPin className="h-4.5 w-4.5 fill-white/20 text-white" size={18} />
          </span>
          <span className="text-xl font-extrabold tracking-tight text-slate-900">GoSpot</span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" data-testid="main-nav">
          {NAV.map((item) => (
            <NavLink key={item.id} to={item.to} end={item.to === "/"} data-testid={`nav-${item.id}`} className={navClass}>
              {t(item.label)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden sm:block">
            <LocationSelector cities={cities} />
          </div>
          <div className="relative">
            <button
              type="button"
              data-testid="lang-button"
              onClick={() => setLangOpen((o) => !o)}
              aria-label={t("Langue")}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:border-blue-200 hover:text-[#2563EB]"
            >
              <Globe className="h-[18px] w-[18px]" />
            </button>
            {langOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setLangOpen(false)} />
                <div data-testid="lang-menu" className="absolute right-0 z-50 mt-2 w-40 rounded-2xl border border-slate-100 bg-white p-2 shadow-xl">
                  <LangDropdown onDone={() => setLangOpen(false)} />
                </div>
              </>
            )}
          </div>
          <button
            type="button"
            data-testid="favorites-button"
            onClick={() => navigate("/favoris")}
            aria-label={t("Mes favoris")}
            className="relative flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:border-red-200 hover:text-red-500"
          >
            <Heart className="h-[18px] w-[18px]" />
            {favCount > 0 && (
              <span data-testid="favorites-count" className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                {favCount}
              </span>
            )}
          </button>
          {user ? (
            <div className="relative">
              <button
                type="button"
                data-testid="user-menu-button"
                onClick={() => setUserOpen((o) => !o)}
                className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-[#2563EB] text-sm font-bold text-white transition hover:bg-[#1D4ED8]"
              >
                {user.avatar ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${user.avatar}`} alt={user.name} className="h-full w-full object-cover" />
                ) : (
                  user.name?.[0]?.toUpperCase() || "U"
                )}
              </button>
              {userOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setUserOpen(false)} />
                  <div data-testid="user-menu" className="absolute right-0 z-50 mt-2 w-56 rounded-2xl border border-slate-100 bg-white p-2 shadow-xl">
                    <div className="px-3 py-2">
                      <p className="truncate text-sm font-semibold text-slate-900">{user.name}</p>
                      <p className="truncate text-xs text-slate-400">{user.email}</p>
                    </div>
                    <button
                      type="button"
                      data-testid="profile-link"
                      onClick={() => {
                        setUserOpen(false);
                        navigate("/profil");
                      }}
                      className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-100"
                    >
                      <User className="h-4 w-4" />
                      {t("Mon profil")}
                    </button>
                    {user.role === "admin" && (
                      <button
                        type="button"
                        data-testid="admin-link"
                        onClick={() => {
                          setUserOpen(false);
                          navigate("/admin");
                        }}
                        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-100"
                      >
                        <ShieldCheck className="h-4 w-4" />
                        {t("Panneau admin")}
                      </button>
                    )}
                    {(user.role === "pro" || user.role === "admin") && (
                      <button
                        type="button"
                        data-testid="pro-space-link"
                        onClick={() => {
                          setUserOpen(false);
                          navigate("/pro/etablissement");
                        }}
                        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-100"
                      >
                        <Building2 className="h-4 w-4" />
                        {t("Mon établissement")}
                      </button>
                    )}
                    <div className="my-1 border-t border-slate-100" />
                    <p className="flex items-center gap-2 px-3 pb-1 pt-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">
                      <Globe className="h-3.5 w-3.5" />
                      {t("Langue")}
                    </p>
                    <LangDropdown onDone={() => setUserOpen(false)} />
                    <div className="my-1 border-t border-slate-100" />
                    <button
                      type="button"
                      data-testid="logout-button"
                      onClick={async () => {
                        setUserOpen(false);
                        await logout();
                        navigate("/");
                      }}
                      className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-100"
                    >
                      <LogOut className="h-4 w-4" />
                      {t("Se déconnecter")}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <button
              type="button"
              data-testid="login-button"
              onClick={() => navigate("/connexion")}
              className="flex items-center gap-2 rounded-full bg-[#2563EB] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1D4ED8] active:scale-95 sm:px-5"
            >
              <User className="h-4 w-4" />
              <span className="hidden min-[420px]:inline">{t("Connexion")}</span>
            </button>
          )}
          <button
            type="button"
            data-testid="mobile-menu-button"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={t("Menu")}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 lg:hidden"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <nav data-testid="mobile-nav" className="border-t border-slate-100 bg-white px-4 py-3 lg:hidden">
          <div className="flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.id}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-${item.id}`}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `rounded-2xl px-4 py-3 text-sm font-medium transition ${
                    isActive ? "bg-[#EFF6FF] text-[#2563EB]" : "text-slate-600 hover:bg-slate-100"
                  }`
                }
              >
                {t(item.label)}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
};
